/**
 * 数据范围表单对话框
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, TreeSelect } from 'antd';
import { message } from '@/utils/antdStatic';
import { createDataScope, updateDataScope } from '../../api/dataPermission';
import { getRoleList } from '../../api/role';
import { getOrganizationTree } from '../../api/organization';
import type { DataScopeRule, DataScopeFormData } from '../../api/types';

interface DataScopeFormProps {
  visible: boolean;
  scope: DataScopeRule | null;
  scopeTypeOptions: { value: string; label: string }[];
  resourceTypes: { value: string; label: string; fields?: string[] }[];
  onCancel: () => void;
  onSuccess: () => void;
}

const DataScopeForm: React.FC<DataScopeFormProps> = ({
  visible,
  scope,
  scopeTypeOptions,
  resourceTypes,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState<{ value: number; label: string }[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [selectedScopeType, setSelectedScopeType] = useState<string>('');
  const isEdit = !!scope;

  // 获取角色列表
  const fetchRoles = async () => {
    try {
      const res = await getRoleList({ current: 1, pageSize: 100 });
      if (res?.data) {
        const roleList = (res.data as any).list ?? (res.data as any).data ?? res.data;
        setRoles((Array.isArray(roleList) ? roleList : []).map((r: any) => ({
          value: r.id,
          label: r.name,
        })));
      }
    } catch (error) {
      console.error('获取角色列表失败', error);
    }
  };

  // 获取部门树
  const fetchDepartments = async () => {
    try {
      const res = await getOrganizationTree();
      if (res?.data) {
        const treeData = (res.data as any).data ?? res.data;
        setDepartments(transformTree(Array.isArray(treeData) ? treeData : []));
      }
    } catch (error) {
      console.error('获取部门树失败', error);
    }
  };

  // 转换树形数据
  const transformTree = (nodes: any[]): any[] => {
    return nodes.map(node => ({
      value: node.id,
      title: node.name,
      children: node.children ? transformTree(node.children) : undefined,
    }));
  };

  useEffect(() => {
    if (visible) {
      fetchRoles();
      fetchDepartments();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && scope) {
      const scopeType = (scope as any).scopeType ?? scope.scope_type;
      const resourceType = (scope as any).resourceType ?? scope.resource_type;
      const roleId = (scope as any).roleId ?? scope.role_id;
      const scopeValue = (scope as any).scopeValue ?? scope.scope_value;
      setSelectedScopeType(scopeType);
      form.setFieldsValue({
        name: (scope as any).name,
        resource_type: resourceType,
        scope_type: scopeType,
        role_id: roleId,
        department_ids: scopeValue ? JSON.parse(scopeValue) : [],
        priority: scope.priority,
        status: scope.status,
        description: (scope as any).description,
      });
    } else if (visible) {
      setSelectedScopeType('');
      form.resetFields();
      form.setFieldsValue({
        status: 1,
        priority: 0,
      });
    }
  }, [visible, scope, form]);

  const handleScopeTypeChange = (value: string) => {
    setSelectedScopeType(value);
    form.setFieldsValue({ department_ids: [] });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      let scopeValue = '';
      if (['department', 'department_tree', 'custom'].includes(values.scope_type)) {
        scopeValue = JSON.stringify(values.department_ids || []);
      }

      const data = {
        name: values.name,
        resource_type: values.resource_type,
        scope_type: values.scope_type,
        scope_value: scopeValue,
        role_id: values.role_id,
        priority: values.priority,
        status: values.status,
        description: values.description,
      } as DataScopeFormData & { name?: string; description?: string };

      if (isEdit) {
        await updateDataScope(scope!.id, data);
        message.success('更新成功');
      } else {
        await createDataScope(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error) {
      console.error('提交失败', error);
    }
  };

  // 是否需要选择部门
  const needDepartmentSelect = ['department', 'department_tree', 'custom'].includes(selectedScopeType);

  return (
    <Modal
      title={isEdit ? '编辑数据范围' : '新增数据范围'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 1,
          priority: 0,
        }}
      >
        <Form.Item
          name="name"
          label="规则名称"
          rules={[{ required: true, message: '请输入规则名称' }]}
        >
          <Input placeholder="请输入规则名称" maxLength={100} />
        </Form.Item>

        <Form.Item
          name="resource_type"
          label="资源类型"
          rules={[{ required: true, message: '请选择资源类型' }]}
        >
          <Select placeholder="请选择资源类型" options={resourceTypes} />
        </Form.Item>

        <Form.Item
          name="scope_type"
          label="范围类型"
          rules={[{ required: true, message: '请选择范围类型' }]}
        >
          <Select
            placeholder="请选择范围类型"
            options={scopeTypeOptions}
            onChange={handleScopeTypeChange}
          />
        </Form.Item>

        {needDepartmentSelect && (
          <Form.Item
            name="department_ids"
            label="选择部门"
            rules={[{ required: true, message: '请选择部门' }]}
          >
            <TreeSelect
              placeholder="请选择部门"
              treeData={departments}
              multiple
              allowClear
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Form.Item
          name="role_id"
          label="适用角色"
        >
          <Select
            placeholder="请选择角色（留空表示适用所有角色）"
            options={roles}
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label="优先级"
          tooltip="数值越大优先级越高"
        >
          <InputNumber min={0} max={100} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Select.Option value={1}>启用</Select.Option>
            <Select.Option value={0}>禁用</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="description"
          label="描述"
        >
          <Input.TextArea
            placeholder="请输入规则描述"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DataScopeForm;
